import React, { useEffect, useState } from "react";
import { Country, State } from "country-state-city";
import Select from "react-select";
import SEO from "../common/SEO";
import HeaderFour from "../common/header/HeaderFour";
import FooterOne from "../common/footer/FooterOne";
import {
  ErrorNotification,
  SuccessNotification,
} from "../tosterNotification/tosters";
import axios from "axios";
import API_URL from "../common/config";

const PriceDiscussion = () => {
  const [formValues, setFormValues] = useState({
    organization: "",
    organizationType: "",
    otherOrganizationType: "",
    contactPerson: "",
    designation: "",
    email: "",
    phone: "",
    country: "",
    state: "",
    website: "",
    source: "",
    comments: "",
  });
  const [logo, setLogo] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [stateOptions, setStateOptions] = useState([]);
  const countries = Country.getAllCountries();

  const countryOptions = countries.map((country) => ({
    value: country.isoCode,
    label: country.name,
  }));

  const organizationTypeOptions = [
    { value: "university", label: "University / College" },
    { value: "corporate", label: "Corporate / Company" },
    { value: "training", label: "Training Institute" },
    { value: "other", label: "Other" },
  ];

  useEffect(() => {
    if (formValues.country) {
      const states = State.getStatesOfCountry(formValues.country).map(
        (state) => ({
          value: state.isoCode,
          label: state.name,
        })
      );
      setStateOptions(states);
      setFormValues((prev) => ({ ...prev, state: "" }));
    } else {
      setStateOptions([]);
      setFormValues((prev) => ({ ...prev, state: "" }));
    }
  }, [formValues.country]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^\+?[\d\s-]{10,}$/.test(phone);

  const handleSelectChange = (selectedOption, { name }) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => {
      const updated = { ...prev };
      if (value) delete updated[name];
      return updated;
    });
  };

  const validateImageFile = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    return file && allowedTypes.includes(file.type);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file && validateImageFile(file)) {
      setLogo(file);
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated.logo;
        return updated;
      });
    } else {
      setLogo(null);
      setFormErrors((prev) => ({
        ...prev,
        logo: "Only JPG, JPEG, PNG image files are allowed.",
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));

    setFormErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (name === "email" && validateEmail(value)) delete updatedErrors.email;
      if (name === "phone" && validatePhone(value)) delete updatedErrors.phone;
      if (name === "organization" && value.trim() !== "")
        delete updatedErrors.organization;
      if (name === "organizationType" && value.trim() !== "")
        delete updatedErrors.organizationType;
      if (name === "otherOrganizationType" && value.trim() !== "")
        delete updatedErrors.otherOrganizationType;
      return updatedErrors;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!formValues.organization.trim())
      errors.organization = "Organization name is required.";
    if (!formValues.organizationType)
      errors.organizationType = "Organization type is required.";
    if (
      formValues.organizationType === "other" &&
      !formValues.otherOrganizationType.trim()
    )
      errors.otherOrganizationType = "Please specify the organization type.";
    if (!formValues.email || !validateEmail(formValues.email))
      errors.email = "Valid email is required.";
    if (!formValues.phone || !validatePhone(formValues.phone))
      errors.phone = "Valid phone number is required.";
    if (!formValues.country) errors.country = "Country is required.";
    if (!formValues.state) errors.state = "State is required.";
    if (!logo) errors.logo = "Logo is required.";

    setFormErrors(errors);
    const formData = new FormData();
    Object.entries(formValues).forEach(([key, value]) =>
      formData.append(key, value)
    );
    formData.append("logo", logo);
    console.table(formData);
    const response = await axios.post(`${API_URL}/price-query/save`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    if (response.data.success) {
      SuccessNotification(
        "Thank you for your query. Our team will get back to you."
      );
      setFormValues({
        organization: "",
        organizationType: "",
        otherOrganizationType: "",
        contactPerson: "",
        designation: "",
        email: "",
        phone: "",
        country: "",
        state: "",
        website: "",
        source: "",
        comments: "",
      });
      setLogo(null);
    } else {
      ErrorNotification("Something wend wrong");
    }
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "38px",
      height: "38px",
      border: "1px solid #ced4da",
      borderRadius: "0.375rem",
      boxShadow: "none",
      backgroundColor: "#fff",
      fontSize: "1rem",
      fontWeight: "400",
      lineHeight: "1.5",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "2px 8px",
      height: "36px",
      display: "flex",
      alignItems: "center",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0",
      padding: "0",
      height: "36px",
      position: "absolute",
    }),
    placeholder: (provided) => ({
      ...provided,
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      margin: "0",
      color: "#6c757d",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    singleValue: (provided) => ({
      ...provided,
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      margin: "0",
      padding: "0",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      position: "absolute",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "150px",
      overflowY: "auto",
      padding: 0,
    }),
    option: (provided) => ({
      ...provided,
      fontSize: "1rem",
      padding: "0.5rem 0.75rem",
      color: "#000",
      cursor: "pointer",
    }),
  };

  return (
    <>
      <SEO />
      <div
        className="form-background-section"
        style={{
          backgroundImage: `url(${
            process.env.PUBLIC_URL + "/images/bg/bg-image-3.jpg"
          })`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          padding: "80px 0",
        }}
      >
        <HeaderFour />
        <div className="form-section" style={{ borderRadius: "12px" }}>
          <div className="main-wrapper">
            <div className="body-wrapper mt-n3">
              <div className="container mt-n5">
                <h5 className="m-3">Expression of Interest Form</h5>
                <div className="form-section card" style={{ height: "auto" }}>
                  <form onSubmit={handleSubmit} encType="multipart/form-data">
                    <div className="row m-1 mb-2 p-3">
                      <div className="col-md-6 mb-3">
                        <label className="small-label fw-bold">
                          Organization Name *
                        </label>
                        <input
                          type="text"
                          name="organization"
                          value={formValues.organization}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Enter organization name"
                        />
                        {formErrors.organization && (
                          <div className="text-danger small mt-1">
                            {formErrors.organization}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="small-label fw-bold">
                          Organization Logo *
                        </label>
                        <input
                          type="file"
                          name="logo"
                          accept=".png,.jpg,.jpeg"
                          onChange={handleLogoChange}
                          className="form-control"
                        />
                        {formErrors.logo && (
                          <div className="text-danger small mt-1">
                            {formErrors.logo}
                          </div>
                        )}
                        {logo && (
                          <div className="mt-2">
                            <img
                              src={URL.createObjectURL(logo)}
                              alt="Preview"
                              height="60"
                            />
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="small-label fw-bold">
                          Type of Organization *
                        </label>
                        <Select
                          name="organizationType"
                          options={organizationTypeOptions}
                          onChange={(option) =>
                            handleSelectChange(option, {
                              name: "organizationType",
                            })
                          }
                          value={
                            organizationTypeOptions.find(
                              (option) =>
                                option.value === formValues.organizationType
                            ) || null
                          }
                          placeholder="Select Organization Type"
                          styles={customSelectStyles}
                          classNamePrefix="select"
                          className="form-control-select"
                        />
                        {formErrors.organizationType && (
                          <div className="text-danger small mt-1">
                            {formErrors.organizationType}
                          </div>
                        )}
                      </div>
                      {formValues.organizationType === "other" && (
                        <div className="col-md-6 mb-3">
                          <label className="small-label fw-bold">
                            Specify Organization Type *
                          </label>
                          <input
                            type="text"
                            name="otherOrganizationType"
                            value={formValues.otherOrganizationType}
                            onChange={handleInputChange}
                            className="form-control"
                            placeholder="Specify organization type"
                          />
                          {formErrors.otherOrganizationType && (
                            <div className="text-danger small mt-1">
                              {formErrors.otherOrganizationType}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="col-md-6 mb-3">
                        <label className="small-label fw-bold">
                          Contact Person Name
                        </label>
                        <input
                          type="text"
                          name="contactPerson"
                          value={formValues.contactPerson}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Enter contact person name"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="small-label fw-bold">
                          Designation / Role
                        </label>
                        <input
                          type="text"
                          name="designation"
                          value={formValues.designation}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Enter designation or role"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="small-label fw-bold">
                          Official Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formValues.email}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Enter your email"
                        />
                        {formErrors.email && (
                          <div className="text-danger small mt-1">
                            {formErrors.email}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="small-label fw-bold">
                          Phone Number *
                        </label>
                        <input
                          type="text"
                          name="phone"
                          value={formValues.phone}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Enter phone number"
                        />
                        {formErrors.phone && (
                          <div className="text-danger small mt-1">
                            {formErrors.phone}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="small-label fw-bold">Country *</label>
                        <Select
                          name="country"
                          options={countryOptions}
                          onChange={(option) =>
                            handleSelectChange(option, { name: "country" })
                          }
                          value={
                            countryOptions.find(
                              (option) => option.value === formValues.country
                            ) || null
                          }
                          placeholder="Select Country"
                          styles={customSelectStyles}
                          classNamePrefix="select"
                          className="form-control-select"
                        />
                        {formErrors.country && (
                          <div className="text-danger small mt-1">
                            {formErrors.country}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="small-label fw-bold">State *</label>
                        <Select
                          name="state"
                          options={stateOptions}
                          onChange={(option) =>
                            handleSelectChange(option, { name: "state" })
                          }
                          value={
                            stateOptions.find(
                              (option) => option.value === formValues.state
                            ) || null
                          }
                          placeholder="Select State"
                          styles={customSelectStyles}
                          classNamePrefix="select"
                          className="form-control-select"
                          isDisabled={!formValues.country}
                        />
                        {formErrors.state && (
                          <div className="text-danger small mt-1">
                            {formErrors.state}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="small-label fw-bold">
                          Website (if any)
                        </label>
                        <input
                          type="text"
                          name="website"
                          value={formValues.website}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Enter website URL"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="small-label fw-bold">
                          How did you hear about CLIF AI?
                        </label>
                        <input
                          type="text"
                          name="source"
                          value={formValues.source}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="E.g., Website, Social Media, Referral"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="small-label fw-bold">
                          Additional Comments / Questions
                        </label>
                        <textarea
                          name="comments"
                          value={formValues.comments}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Enter any additional comments or questions"
                          style={{
                            // height: "50px",
                            // minHeight: "50px",
                            padding: "4px",
                            minWidth: "500px",
                            maxWidth: "100%",
                            width: "100%",
                          }}
                          rows={4}
                        />
                      </div>
                      <div className="col-md-12 d-flex justify-content-center">
                        <button
                          type="submit"
                          className="btn c-white"
                          style={{
                            width: "50%",
                            display: "flex",
                            justifyContent: "center",

                            color: "white",
                            backgroundColor: "#6600CC",
                            fontSize: "16px",
                          }}
                          onMouseOver={(e) => {
                            e.target.style.background = "#006600";
                            e.target.style.boxShadow =
                              "0 3px 6px rgba(108, 92, 231, 0.4)";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = "#6600CC";
                            e.target.style.boxShadow =
                              "0 2px 4px rgba(108, 92, 231, 0.3)";
                          }}
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="mt-5" />
          </div>
        </div>
        <FooterOne />
      </div>
    </>
  );
};

export default PriceDiscussion;
